<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div class="header-extra">
        <ColumnToggle
          :table-name="'accountUsers'"
          :user-settings="userSettings"
          :settings-status="getUserSettingsStatus"
          :column-settings="columnSettings"
          @onChange="updateUserVisibleColumns"
        />
        <button
          class="m-r-10 btn btn-sm widget-header-button"
          @click="exportUsersCSV()"
        >
          {{ $t('ACTION_EXPORT_CSV') }}
        </button>
        <v-select
          v-model="filters.role.value"
          :options="options"
          :clearable="false"
          :searchable="false"
          class="m-r-10 btn-xl pull-right widget-header-dropdown"
        />
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('PLACEHOLDER_USERS_SEARCH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!loading"
      class="m-b-15"
    >
      <button
        type="button"
        :disabled="showAddNewForm"
        class="btn btn-success btn-sm"
        @click="displayNewForm"
      >
        <i class="fa fa-plus" />
        <span v-t="'ACTION_INVITE'" />
      </button>
    </div>
    <div class="grid_table_holder">
      <v-table
        :data="usersAndInvites"
        :current-page.sync="currentPage"
        :page-size="20"
        :filters="filters"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />    
            <v-th
              v-for="col in userVisibleColumns"
              :key="col"
              v-t="columnSettings[col].displayName"
              :class="columnSettings[col].class"
              :default-sort="columnSettings[col].defaultSort"
              :sort-key="columnSettings[col].name"
            />
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <tr
            v-if="showAddNewForm"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <InviteUserForm
                :show-loader="inviteUserStatus.status === 'pending'"
                @cancel="hideNewForm"
                @submit="onInviteUser"
              />
            </td>
          </tr>
          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionId == row.id)"
              :key="row.id"
              :class="{'bg-row-odd': index % 2 === 0, 'bg-row-even': index % 2 !== 0 }"
            >
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  :disabled="showAddNewForm"
                  @click="displayMobileAction(row.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>
              <td
                v-if="userVisibleColumns.includes('name')"
                :class="{clickable: !showAddNewForm}"
                @click="!showAddNewForm"
              >
                {{ row.name }}
              </td>
              <td v-if="userVisibleColumns.includes('email')">
                {{ row.email }}
              </td>
              <td v-if="userVisibleColumns.includes('role')">
                {{ row.roleDisplay }}
              </td>
              <td v-if="userVisibleColumns.includes('invite')">
                {{ row.inviteStatus }}
              </td>
              <td
                v-if="userVisibleColumns.includes('canExportReport')"
                class="text-center"
              >
                <i
                  v-if="row.canExportReport === false"
                  class="fa fa-times text-danger"
                />
                <i
                  v-if="row.canExportReport === true "
                  class="fa fa-check text-success"
                />
              </td>
              <td v-if="!isMobile">
                <UsersActions
                  :user="row"
                  :disabled="showAddNewForm"
                  @delete-user="onDeleteUser"
                  @delete-invite="onDeleteInvite"
                  @copied-invitation-url="onCopiedInvitarionUrl"
                />
              </td>
            </tr>
            <tr
              v-if="isMobile && activeActionId == row.id"
              :key="row.id + '-mobile'"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <UsersActions
                  :user="row"
                  :disabled="showAddNewForm"
                  @collapse-actions="!showAddNewForm && hideMobileAction()"
                  @delete-user="onDeleteUser"
                  @delete-invite="onDeleteInvite"
                  @copied-invitation-url="onCopiedInvitarionUrl"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
    </div>
    <div class="pagination-min">
      <div class="text-center p-7">
        <smart-pagination
          :current-page.sync="currentPage"
          :total-pages="totalPages"
          :max-page-links="20"
        />
      </div>
    </div>

    <div
      v-if="loading"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'USERS_TABLE_LOADING'" />
    </div> 
    <div
      v-if="emptyUsers"
      v-t="'USERS_TABLE_EMPTY'"
      class="well well-sm text-center"
    /> 
  </PortalWidget>
</template>

<script>
import UsersActions from './UsersActions';
import InviteUserForm from './InviteUserForm';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import { isMobile } from '@/core/utilities';
import { accountUserService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapActions, mapState, mapGetters } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';

export default {
  name: 'Users',
  components: {
    InviteUserForm,
    UsersActions
  },
  props: { },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      showAddNewForm: false,
      isMobile: isMobile,
      activeActionId: null,
      widget: {
        title:this.$i18n.t('MENU_LEFT_MANAGE_USERS'),
        iconClass: 'fa fa-user-plus'
      },
      filters: {
        name: { value: '', keys: ['name','email','roleDisplay'] },        
        role: { value: { code: '', label: 'All Users' }, custom: this.roleFilter }
      }, 
      options: [
        {code: '', label: 'All Users'},
        {code: 'Admin', label: 'Admin'},
        {code: 'Publisher (', label: 'Publishers'},
        {code: 'Buyer (', label: 'Buyers'},
        {code: 'Publisher Manager', label: 'Publisher Managers'},
        {code: 'Buyer Manager', label: 'Buyer Managers'},
        {code: 'Report Access', label: 'Report Access'},
      ],
      selectedOptionDropdown: ''
    };
  },
  computed: {
    ...mapState('users', ['users', 'invites', 'publishers', 'buyers', 'error', 'userVisibleColumns']),
    ...mapState('ui', ['userSettings']),
    ...mapGetters('users', [
      'getUsersStatus',
      'getInvitesStatus',
      'inviteUserStatus',
      'deleteUserStatus',
      'deleteInviteStatus',
      'getUserVisibleColumnsStatus']),
    ...mapGetters('ui', [
      'getUserSettingsStatus'
    ]),
    loading() {
      return this.getUsersStatus.status === STATUS_PENDING ||
      this.getInvitesStatus.status === STATUS_PENDING;
    },
    emptyUsers() {
      return this.getUsersStatus.status === STATUS_SUCCESS
      && this.users
      && this.users.length === 0
      && this.getInvitesStatus.status === STATUS_SUCCESS
      && this.invites
      && this.invites.length === 0;
    },
    columnSettings() {
      return accountUserService.columnSettings;
    },
    usersAndInvites() {
      if(!this.loading) {
        return this.mergeInvitationsIntoUsersCollection();
      }
      return [];
    }
  },
  async created() {
    this.getPublishers();
    this.getBuyers();
    this.getUsers();
    this.getInvites();
    this.getUserSettings();
  },
  methods: {
    ...mapActions('users', [
      'getUserVisibleColumns',
      'getUsers',
      'getInvites',
      'getPublishers',
      'getBuyers',
      'inviteUser',
      'deleteUser',
      'deleteInvite',
      'exportCSVFile'
    ]),
    ...mapActions('ui', [
      'getUserSettings'
    ]),
    updateUserVisibleColumns() {
      this.getUserVisibleColumns();
    },
    roleFilter (filterValue, row) {
      return row.roleDisplay.includes(filterValue.code);
    },
    onOptionChange(option) {
      this.filters.role.value = option.code;
    },
    onCopiedInvitarionUrl() {
      utilities.showToaster('success', this.$i18n.t('MESSAGE_COPY_INVITE_URL_SUCCESS'));
    },
    displayNewForm() {
      this.showAddNewForm = true;
    },
    hideNewForm() {
      this.showAddNewForm = false;
    },
    displayMobileAction(rowId) {
      this.activeActionId = rowId;
    },
    hideMobileAction() {
      this.activeActionId = null;
    },
    async onInviteUser(invite) {
      await this.inviteUser(invite);
      if (this.inviteUserStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('ALERT_INVITE_SUCCESS'));
        this.showAddNewForm = false;
      }
      if (this.inviteUserStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.inviteUserStatus.error);
      }
    },
    async onDeleteUser(id) {
      await this.deleteUser(id);
      if (this.deleteUserStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_REMOVE_USER_SUCCESSFUL'));
      }
      if (this.deleteUserStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.error);
      }
    },
    async onDeleteInvite(id) {
      await this.deleteInvite(id);
      if (this.deleteInviteStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_REMOVE_INVITATION_SUCCESSFUL'));
      }
      if (this.deleteInviteStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.error);
      }
    },
    async exportUsersCSV(){
      var filter = this.filters.role.value;
      var usersToExport = _.filter(this.usersAndInvites, (user) => user.roleDisplay.includes(filter.code));
      await this.exportCSVFile(usersToExport, 'users');      
    },
    mergeInvitationsIntoUsersCollection() {
      let usersAndInvites = [];
      _.forEach(this.users, (user) => {
        var invite = _.find(this.invites, { email: user.email });
        
        if (invite) {

          // ! assign a non circular reference to user
          invite.user = JSON.parse(JSON.stringify(user));
          user.invite = invite;
          user.wasInvited = true;
          user.inviteStatus = invite.status;
        }

        user.inviteStatus = 'Accepted';
        user.hasUser = true;
        user.hasInvite = !!invite;
        user.roleDisplay = this.getDisplayRole(user);
        user.nameAndNumber = this.getNameAndNumber(user);
        usersAndInvites.push(user);
      });

      // Create users records that represent invitations
      _.forEach(this.invites, (invite) => {
        if (!invite.user) {
          let user = {
            id: invite.id,
            name: invite.firstName + ' ' + invite.lastName,
            firstName: invite.firstName,
            lastName: invite.lastName,
            email: invite.email,
            inviteStatus: invite.status,
            canExportReport:  this.canInviteeExportReports(invite),
            role: invite.role,
            associatedRoleIds: invite.associatedRoleIds,
            hasUser: false,
            hasInvite: true,
            invite: invite
          };
            
          user.roleDisplay = this.getDisplayRole(user);
          user.nameAndNumber = this.getNameAndNumber(user);
          usersAndInvites.push(user);
        }
      });

      return usersAndInvites;
    },
    getNameAndNumber(user) {
      return user.name
        + ((user.mobileNumber && user.mobileNumber.length)
          ? ' (' + user.mobileNumber + ')'
          : '');
    },
    getDisplayRole(user) {
      if (!user) {
        return;
      }

      var _getIdsNames = (user) => {
        if (user.associatedRoleIds && user.associatedRoleIds.length) {
          return ' (' + _.map(user.associatedRoleIds, (id) => {
            return user.role == 'buyer'
              ? (this.buyers[id] ? this.buyers[id].name : id)
              : (this.publishers[id] ? this.publishers[id].name : id);
          }).join(', ') + ')';
        }

        return '';
      };

      if (user.role == 'admin') {
        return this.$t('ACCOUNT_USER_ADMIN_ROLE');
      }

      if (user.role == 'user') {
        return this.$t('ACCOUNT_USER_REPORT_ROLE');
      }

      if (user.role == 'buyer') {
        return this.$t('ACCOUNT_USER_BUYER_ROLE') + _getIdsNames(user);
      }

      if (user.role == 'affiliate') {
        return this.$t('ACCOUNT_USER_PUBLISHER_ROLE') + _getIdsNames(user);
      }

      if (user.role == 'buyer_manager') {
        return this.$t('ACCOUNT_USER_BUYER_MANAGER_ROLE');
      }

      if (user.role == 'affiliate_manager') {
        return this.$t('ACCOUNT_USER_PUBLISHER_MANAGER_ROLE');
      }

      return user.role;
    },
    canInviteeExportReports(invite) {
      return invite.role == 'admin'
        || invite.role == 'affiliate'
        || invite.role == 'buyer'
        || (invite.role == 'user' 
          && invite.optionalPermissions != null 
          && _.indexOf(invite.optionalPermissions, 'report_export') >= 0);
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>